// i18nProvider wiring for react-admin.
//
// - Czech is the default locale (per product decision).
// - The active locale is persisted in localStorage under `faust.locale`.
// - Per-locale dictionaries merge the react-admin built-in messages with the
//   app-specific dictionary. Slovak has no maintained ra-language-* package,
//   so we layer hand-rolled overrides on top of the English base.

import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';
import czechMessages from 'ra-language-czech';

import enApp from './messages/en';
import csApp from './messages/cs';
import skApp from './messages/sk';
import slovakRaOverrides from './raSlovak';

export const SUPPORTED_LOCALES = ['cs', 'sk', 'en'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'cs';
const STORAGE_KEY = 'faust.locale';

const messagesByLocale: Record<AppLocale, any> = {
    cs: { ...czechMessages, ...csApp },
    sk: { ...englishMessages, ...slovakRaOverrides, ...skApp },
    en: { ...englishMessages, ...enApp },
};

const isSupported = (value: string | null): value is AppLocale =>
    !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const getInitialLocale = (): AppLocale => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isSupported(stored)) return stored;
    } catch {
        // localStorage may be unavailable (SSR, privacy mode); fall through.
    }
    return DEFAULT_LOCALE;
};

export const persistLocale = (locale: AppLocale): void => {
    try {
        localStorage.setItem(STORAGE_KEY, locale);
    } catch {
        // Ignore: persistence is best-effort.
    }
};

export const i18nProvider = polyglotI18nProvider(
    locale => messagesByLocale[(locale as AppLocale)] ?? messagesByLocale[DEFAULT_LOCALE],
    getInitialLocale(),
    [
        { locale: 'cs', name: 'Čeština' },
        { locale: 'sk', name: 'Slovenčina' },
        { locale: 'en', name: 'English' },
    ],
    { allowMissing: true }
);
